import React, { useCallback, useState } from 'react';

import { useOrionSettings } from '@vimeo/orion';

import UpsellModal from '../upsell-modal';
import { DEFAULT_SEAT_PLANS, LEGACY_PLANS, NEW_PLANS } from '../utils/constants';
import { modalProps, TrackingProps } from '../utils/interface';
import { isBspLateStagePaywallKind } from '../utils/lateStagePaywallResolver';

type UseLateStagePaywallLauncherArgs = {
  apiUrl?: string;
  userConfig: modalProps['userConfig'];
  modalConfig?: modalProps['modalConfig'];
};

type UseLateStagePaywallLauncher = {
  isOpen: boolean;
  close: () => void;
  openFromUpgradePlanUrl: (url: string) => boolean;
  lateStagePaywallModal: React.JSX.Element | null;
};

export const useLateStagePaywallLauncher = ({
  apiUrl,
  userConfig,
  modalConfig,
}: UseLateStagePaywallLauncherArgs): UseLateStagePaywallLauncher => {
  const { settings } = useOrionSettings();
  const [tracking, setTracking] = useState<TrackingProps | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openFromUpgradePlanUrl = useCallback(
    (url: string): boolean => {
      if (
        !isBspLateStagePaywallKind(settings.late_stage_paywall_kind) ||
        !userConfig.jwt ||
        !isLateStageSupportedAccountType(userConfig.accountType)
      ) {
        return false;
      }

      const trackingParams = parseUpgradePlanUrlTrackingParams(url);

      if (!trackingParams) {
        return false;
      }

      setTracking(trackingParams);
      setIsOpen(true);
      return true;
    },
    [settings.late_stage_paywall_kind, userConfig.jwt, userConfig.accountType],
  );

  return {
    isOpen,
    close,
    openFromUpgradePlanUrl,
    lateStagePaywallModal:
      isOpen && tracking ? (
        <UpsellModal
          apiUrl={apiUrl}
          userConfig={userConfig}
          templateType="default"
          onClose={close}
          modalConfig={modalConfig}
          tracking={tracking}
        />
      ) : null,
  };
};

const UPGRADE_PLAN_PATH = '/upgrade-plan';
const LATE_STAGE_SUPPORTED_ACCOUNT_TYPES = new Set<string>([
  ...DEFAULT_SEAT_PLANS,
  ...LEGACY_PLANS,
  ...NEW_PLANS,
]);
const isLateStageSupportedAccountType = (accountType?: string) =>
  accountType !== undefined && LATE_STAGE_SUPPORTED_ACCOUNT_TYPES.has(accountType);

const parseUpgradePlanUrlTrackingParams = (rawUrl: string): TrackingProps | null => {
  try {
    const url = new URL(
      rawUrl,
      typeof window !== 'undefined' ? window.location.origin : 'https://vimeo.com',
    );

    if (url.pathname !== UPGRADE_PLAN_PATH) {
      return null;
    }

    const searchParams = url.searchParams;
    const paywall = searchParams.get('paywall');
    const paywallTrigger = searchParams.get('paywall_trigger');
    const paywallLocation = searchParams.get('paywall_location');
    const paywallFeature = searchParams.get('paywall_feature');

    if (paywall !== '1' || !paywallTrigger || !paywallLocation || !paywallFeature) {
      return null;
    }

    const feature = searchParams.get('feature');
    const location = searchParams.get('location');

    return {
      params: {
        upsell_name: searchParams.get('upsell'),
        page: searchParams.get('page') ?? paywallLocation,
        feature: feature ?? paywallFeature,
        location: location ?? paywallLocation,
        target: UPGRADE_PLAN_PATH,
        integration: searchParams.get('integration'),
      },
      paywallTracking: {
        paywallTrigger,
        paywallLocation,
        paywallType: 'popup',
        paywallFeature,
      },
    };
  } catch (_error) {
    return null;
  }
};
